import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || '';
const API_URL = `${BASE_URL}/api`;
const AUTH_URL = `${BASE_URL}/api/auth`;

// Token management
let accessToken = null;
let refreshPromise = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const getRefreshToken = () => localStorage.getItem('refreshToken');

export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem('refreshToken', token);
  } else {
    localStorage.removeItem('refreshToken');
  }
};

export const clearTokens = () => {
  accessToken = null;
  localStorage.removeItem('refreshToken');
};

// Create axios instances
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const authApi = axios.create({
  baseURL: AUTH_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Refresh token function
const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new Error('No refresh token');
  }

  try {
    const response = await axios.post(`${AUTH_URL}/token/refresh/`, {
      refresh: refreshToken,
    });

    const { access, refresh } = response.data;
    setAccessToken(access);
    setRefreshToken(refresh); // Save rotated refresh token
    return access;
  } catch (error) {
    clearTokens();
    throw error;
  }
};

// Request interceptor - add auth header
const addAuthHeader = (config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
};

api.interceptors.request.use(addAuthHeader);
authApi.interceptors.request.use(addAuthHeader);

// Response interceptor - handle 401 and refresh token
const handle401 = async (error) => {
  const originalRequest = error.config;

  // If error is 401 and we haven't tried to refresh yet
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;

    // If already refreshing, wait for that to complete
    if (refreshPromise) {
      try {
        await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axios(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    // Try to refresh token
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      try {
        refreshPromise = refreshAccessToken();
        await refreshPromise;
        refreshPromise = null;

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        refreshPromise = null;
        clearTokens();
        // Dispatch event for auth context to handle logout
        window.dispatchEvent(new CustomEvent('auth:logout'));
        return Promise.reject(refreshError);
      }
    }
  }

  return Promise.reject(error);
};

api.interceptors.response.use((response) => response, handle401);
authApi.interceptors.response.use((response) => response, handle401);

// Helper to extract data from API response (handles both {data: [...]} and direct array)
const extractData = (response) => {
  const data = response.data;
  // If response has a 'data' property that's an array, return it
  if (data && data.data && Array.isArray(data.data)) {
    return { ...response, data: data.data };
  }
  // If response has 'results' (pagination), return results
  if (data && data.results && Array.isArray(data.results)) {
    return { ...response, data: data.results };
  }
  return response;
};

// Helper for single object responses
const extractSingleData = (response) => {
  const data = response.data;
  if (data && data.data && !Array.isArray(data.data)) {
    return { ...response, data: data.data };
  }
  return response;
};

export const playersAPI = {
  getAll: () => api.get('/players/').then(extractData),
  getAllPublic: () => api.get('/players/?public=true').then(extractData),
  getDropdown: () => api.get('/players/dropdown/').then(extractData),
  get: (id) => api.get(`/players/${id}/`).then(extractSingleData),
  create: (data) => {
    const isFormData = data instanceof FormData;
    return api.post('/players/', data, isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {});
  },
  update: (id, data) => {
    const isFormData = data instanceof FormData;
    return api.put(`/players/${id}/`, data, isFormData ? {
      headers: { 'Content-Type': 'multipart/form-data' }
    } : {});
  },
  delete: (id) => api.delete(`/players/${id}/`),
  getStats: (id) => api.get(`/players/${id}/stats/`).then(extractSingleData),
};

export const tournamentsAPI = {
  getAll: () => api.get('/tournaments/').then(extractData),
  getAllPublic: () => api.get('/tournaments/?public=true').then(extractData),
  get: (id) => api.get(`/tournaments/${id}/`).then(extractSingleData),
  getPublic: (id) => api.get(`/tournaments/${id}/?public=true`).then(extractSingleData),
  create: (data) => api.post('/tournaments/', data),
  update: (id, data) => api.put(`/tournaments/${id}/`, data),
  delete: (id) => api.delete(`/tournaments/${id}/`),
  addPlayer: (id, playerId, seed) => api.post(`/tournaments/${id}/add_player/`, { player_id: playerId, seed }),
  removePlayer: (id, playerId) => api.post(`/tournaments/${id}/remove_player/`, { player_id: playerId }),
  replacePlayer: (id, oldPlayerId, newPlayerId) => api.post(`/tournaments/${id}/replace_player/`, { old_player_id: oldPlayerId, new_player_id: newPlayerId }),
  withdrawPlayer: (id, playerId) => api.post(`/tournaments/${id}/withdraw_player/`, { player_id: playerId }),
  generateBracket: (id) => api.post(`/tournaments/${id}/generate_bracket/`),
  getBracket: (id) => api.get(`/tournaments/${id}/bracket/`),
  setManualSeeds: (id, seeds, byeStrategy) => api.post(`/tournaments/${id}/set_manual_seeds/`, { seeds, bye_strategy: byeStrategy }),
  getBracketPreview: (id) => api.get(`/tournaments/${id}/bracket_preview/`),
  swapMatches: (id, matchAId, matchBId) => api.post(`/tournaments/${id}/swap_matches/`, {
    match_a_id: matchAId,
    match_b_id: matchBId,
  }),
  swapPlayers: (id, sourceMatchId, sourceSlot, destMatchId, destSlot) => api.post(`/tournaments/${id}/swap_players/`, {
    source_match_id: sourceMatchId,
    source_slot: sourceSlot,
    dest_match_id: destMatchId,
    dest_slot: destSlot,
  }),
  lockBracket: (id) => api.post(`/tournaments/${id}/lock_bracket/`),
};

export const matchesAPI = {
  getAll: () => api.get('/matches/').then(extractData),
  get: (id) => api.get(`/matches/${id}/`).then(extractSingleData),
  getRecent: (limit = 5) => api.get(`/matches/recent/?limit=${limit}`).then(extractData),
  updateScore: (id, sets) => api.post(`/matches/${id}/update_score/`, { sets }),
  resetScore: (id) => api.post(`/matches/${id}/reset_score/`),
  forfeit: (id, forfeitBy, forfeitType) => api.post(`/matches/${id}/forfeit/`, { forfeit_by: forfeitBy, forfeit_type: forfeitType }),
  getHeadToHead: (player1Id, player2Id) => api.get(`/matches/head-to-head/?player1=${player1Id}&player2=${player2Id}`),
};

export const leaguesAPI = {
  getAll: () => api.get('/leagues/').then(extractData),
  getAllPublic: () => api.get('/leagues/?public=true').then(extractData),
  get: (id) => api.get(`/leagues/${id}/`).then(extractSingleData),
  getPublic: (id) => api.get(`/leagues/${id}/?public=true`).then(extractSingleData),
  create: (data) => api.post('/leagues/', data),
  update: (id, data) => api.put(`/leagues/${id}/`, data),
  delete: (id) => api.delete(`/leagues/${id}/`),
  getStandings: (id) => api.get(`/leagues/${id}/standings/`).then(extractSingleData),
  getGroups: (id) => api.get(`/leagues/${id}/groups/`).then(extractData),
  getMatches: (id, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.group) params.append('group', filters.group);
    if (filters.round) params.append('round', filters.round);
    if (filters.all) params.append('all', 'true');
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/leagues/${id}/matches/${query}`).then(extractData);
  },
  getRoundsInfo: (id) => api.get(`/leagues/${id}/rounds_info/`),
  generateMatches: (id) => api.post(`/leagues/${id}/generate_matches/`),
  addPlayer: (id, playerId, groupId) => api.post(`/leagues/${id}/add_player/`, { player_id: playerId, group_id: groupId }),
  removePlayer: (id, playerId) => api.post(`/leagues/${id}/remove_player/`, { player_id: playerId }),
  assignGroup: (id, leaguePlayerId, groupId) => api.post(`/leagues/${id}/assign_group/`, { league_player_id: leaguePlayerId, group_id: groupId }),
  updatePenalty: (id, leaguePlayerId, pointDeduction) => api.post(`/leagues/${id}/update_penalty/`, { league_player_id: leaguePlayerId, point_deduction: pointDeduction }),
  replacePlayer: (id, oldPlayerId, newPlayerId) => api.post(`/leagues/${id}/replace_player/`, { old_player_id: oldPlayerId, new_player_id: newPlayerId }),
  withdrawPlayer: (id, playerId) => api.post(`/leagues/${id}/withdraw_player/`, { player_id: playerId }),
  createGroups: (id) => api.post(`/leagues/${id}/create_groups/`),
  finalize: (id) => api.post(`/leagues/${id}/finalize/`),
  startKnockout: (id) => api.post(`/leagues/${id}/start_knockout/`),
};

export const rankingsAPI = {
  getAll: () => api.get('/rankings/').then(extractData),
  getTop: (n) => api.get(`/rankings/top/?limit=${n}`).then(extractData),
  recalculate: () => api.post('/rankings/update_all/'),
};

export const powerIndexAPI = {
  getAll: () => api.get('/power-index/').then(extractData),
  recalculate: () => api.post('/power-index/recalculate/'),
};

export const newsAPI = {
  getAll: () => api.get('/news/').then(extractData),
  get: (id) => api.get(`/news/${id}/`).then(extractSingleData),
  getFeatured: () => api.get('/news/featured/').then(extractData),
  create: (data) => api.post('/news/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/news/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/news/${id}/`),
};

export const authAPI = {
  login: async (username, password) => {
    const response = await axios.post(`${AUTH_URL}/login/`, { username, password });
    const { access, refresh, user } = response.data;
    setAccessToken(access);
    setRefreshToken(refresh);
    return { data: { user, access, refresh } };
  },
  logout: async () => {
    const refreshToken = getRefreshToken();
    try {
      await authApi.post('/logout/', { refresh: refreshToken });
    } finally {
      clearTokens();
    }
  },
  getCurrentUser: () => authApi.get('/me/'),
  getDashboardStats: () => authApi.get('/dashboard-stats/'),
  changePassword: (oldPassword, newPassword, newPasswordConfirm) =>
    authApi.post('/users/change_password/', {
      old_password: oldPassword,
      new_password: newPassword,
      new_password_confirm: newPasswordConfirm
    }),
  refreshToken: refreshAccessToken,
};

export const usersAPI = {
  getAll: () => authApi.get('/users/').then(extractData),
  get: (id) => authApi.get(`/users/${id}/`).then(extractSingleData),
  create: (data) => authApi.post('/users/', data),
  update: (id, data) => authApi.put(`/users/${id}/`, data),
  delete: (id) => authApi.delete(`/users/${id}/`),
};

export const activityAPI = {
  getAll: () => authApi.get('/activity/').then(extractData),
};

export const adsAPI = {
  getAll: () => api.get('/ads/').then(extractData),
  get: (id) => api.get(`/ads/${id}/`).then(extractSingleData),
  create: (data) => api.post('/ads/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/ads/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/ads/${id}/`),
  getActive: () => api.get('/ads/active/').then(extractData),
  getByPosition: () => api.get('/ads/by_position/').then(extractSingleData),
  trackClick: (id) => api.post(`/ads/${id}/track_click/`),
};

export const clubsAPI = {
  getAll: () => api.get('/clubs/').then(extractData),
  get: (id) => api.get(`/clubs/${id}/`).then(extractSingleData),
  create: (data) => api.post('/clubs/', data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, data) => api.put(`/clubs/${id}/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/clubs/${id}/`),
  getActive: () => api.get('/clubs/active/').then(extractData),
  addImage: (id, data) => api.post(`/clubs/${id}/add_image/`, data, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  removeImage: (id, imageId) => api.post(`/clubs/${id}/remove_image/`, { image_id: imageId }),
  setMainImage: (id, imageId) => api.post(`/clubs/${id}/set_main_image/`, { image_id: imageId }),
};

export const publicStatsAPI = {
  get: () => api.get('/stats/'),
};

export const analyticsAPI = {
  trackVisit: (path = '/') => api.post('/auth/track-visit/', { path }),
  getStats: () => authApi.get('/visit-stats/'),
};

export default api;
